import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { CalendarDays, Calendar } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type PeriodType = 'week' | 'month' | 'year' | 'custom';

export interface PeriodValue {
  type: PeriodType;
  startDate?: string;
  endDate?: string;
}

interface PeriodSelectorProps {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  className?: string;
}

const PERIOD_LABELS: Record<PeriodType, string> = {
  week: 'Неделя',
  month: 'Месяц',
  year: 'Год',
  custom: 'Произвольный',
};

export function PeriodSelector({ value, onChange, className }: PeriodSelectorProps) {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: value.startDate ? new Date(value.startDate) : undefined,
    to: value.endDate ? new Date(value.endDate) : undefined,
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const handlePeriodChange = useCallback((type: PeriodType) => {
    if (type === 'custom') {
      onChange({
        type: 'custom',
        startDate: dateRange.from?.toISOString(),
        endDate: dateRange.to?.toISOString(),
      });
    } else {
      onChange({ type });
    }
  }, [onChange, dateRange]);

  const handleDateSelect = useCallback((range: { from: Date | undefined; to: Date | undefined }) => {
    setDateRange(range);
    if (range.from && range.to) {
      onChange({
        type: 'custom',
        startDate: range.from.toISOString(),
        endDate: range.to.toISOString(),
      });
      setIsCalendarOpen(false);
    }
  }, [onChange]);

  const formatDateRange = () => {
    if (!dateRange.from) {return 'Выберите даты';}
    if (!dateRange.to) {return format(dateRange.from, 'd MMM yyyy', { locale: ru });}
    return `${format(dateRange.from, 'd MMM', { locale: ru })} - ${format(dateRange.to, 'd MMM yyyy', { locale: ru })}`;
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Select
        value={value.type}
        onValueChange={(v) => handlePeriodChange(v as PeriodType)}
      >
        <SelectTrigger className="w-[180px]">
          <CalendarDays className="h-4 w-4 mr-2" />
          <SelectValue placeholder="Выберите период" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <SelectItem key={key} value={key}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value.type === 'custom' && (
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                'w-[260px] justify-start text-left font-normal',
                !dateRange.from && 'text-muted-foreground'
              )}
            >
              <Calendar className="mr-2 h-4 w-4" />
              {formatDateRange()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              initialFocus
              mode="range"
              defaultMonth={dateRange.from}
              selected={dateRange}
              onSelect={(range) => handleDateSelect({ from: range?.from, to: range?.to })}
              numberOfMonths={2}
              locale={ru}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
